import { SelectionModel } from "@angular/cdk/collections";
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	TemplateRef,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { MatTooltip } from "@angular/material/tooltip";
import { Select } from "@ngxs/store";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { distinctUntilChanged, filter, mergeMap, switchMap, takeUntil } from "rxjs/operators";
import { AppState } from "src/app/app.state";
import { ConfirmDeleteRuleDialogComponent } from "src/app/components/confirm-delete-rule-dialog/confirm-delete-rule-dialog.component";
import { TableComponent } from "src/app/components/table/table.component";
import { MirRule, MirRuleHttp, RuleApiParams } from "src/app/models/mir-rule.model";
import { RulesService } from "src/app/services/rules/rules.service";
import { isDefined } from "src/utils/isDefined/isDefined";

export interface RestorePageContext {
	ruleApiParams: RuleApiParams;
}

export interface RulesTableClickEvent {
	rule: MirRule;
	context?: RestorePageContext;
}

@Component({
	selector: "app-rules-table",
	templateUrl: "./rules-table.component.html",
	styleUrls: ["./rules-table.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class RulesTableComponent implements OnInit, OnDestroy, OnChanges {
	constructor(private rulesService: RulesService, private dialog: MatDialog, private cdr: ChangeDetectorRef) {}

	@Input() context: RuleApiParams;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild("ruleErrors") ruleErrorTemplate: TemplateRef<any>;
	@ViewChild(TableComponent) table: TableComponent;
	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;

	@Output() rowClicked = new EventEmitter<RulesTableClickEvent>();
	@Output() selectedRowsEmitter = new EventEmitter<MirRule[]>();
	@Output() rowsDeleted = new EventEmitter<void>();

	dataSource: MatTableDataSource<MirRule>;
	private onDestroy$ = new Subject<void>();

	pageSize = 10;

	companyId: string;

	private _ruleApiParams$: BehaviorSubject<RuleApiParams> = new BehaviorSubject({
		page: "1",
		perPage: this.pageSize.toString(),
	});
	pageSizeOptions = [10, 25, 50, 75, 100];
	selectionModel: SelectionModel<any>;
	sort: MatSort;
	paginatorLength = 0;
	view = "rules";

	currentRuleApiParams: RuleApiParams = {
		page: "1",
		perPage: this.pageSize.toString(),
		active: true,
		sort: "ruleName",
		sortDir: "asc",
	};
	selectedRows: MirRule[] = [];
	failedRuleNames: string = null;
	error = false;

	onRowClick(rule: MirRule) {
		this.rowClicked.emit({
			rule,
			context: { ruleApiParams: { ...this.currentRuleApiParams } },
		});
	}
	selectedRowsReceived(rows: MirRule[]) {
		this.selectedRows = rows;
		this.selectedRowsEmitter.emit(this.selectedRows);
	}
	ngOnChanges(changes: SimpleChanges): void {
		if (changes.context.currentValue) {
			const oldParams = { ...this.currentRuleApiParams };
			const newParams = { ...changes.context.currentValue.ruleApiParams };

			// Compare objects to see if they are different
			let shouldSendParams = false;

			// Get all unique keys from both objects
			const oldKeys = Object.keys(oldParams);
			const newKeys = Object.keys(newParams);
			const allKeys = [...new Set([...oldKeys, ...newKeys])];

			// Check if any property is different
			for (const key of allKeys) {
				if (oldParams[key] !== newParams[key]) {
					shouldSendParams = true;
					break;
				}
			}

			// only next the rule api params if the params are different to avoid constant calls to get rules  from the api, which is what happened with the original change of this

			if (shouldSendParams) {
				this.currentRuleApiParams = newParams;
				this._ruleApiParams$.next(this.currentRuleApiParams);
			}

			if (this.paginator) {
				this.paginator.pageIndex = this.currentRuleApiParams.page;
			}
		}
	}
	ngOnInit(): void {
		this.dataSource = new MatTableDataSource();

		combineLatest([this.selectedCompanyId$.pipe(distinctUntilChanged()), this._ruleApiParams$])
			.pipe(
				filter(([companyId]) => isDefined(companyId)),
				mergeMap(([companyId, ruleApiParams]) => {
					this.companyId = companyId;
					const newParams = {
						...this.currentRuleApiParams,
						...ruleApiParams,
					};

					return this.rulesService.getRules(companyId, newParams);
				}),
				takeUntil(this.onDestroy$),
			)
			.subscribe(
				rules => {
					this.selectedRows = [];
					this.paginatorLength = parseInt(rules.headers.get("X-Total-Count"));
					const data = this.getRulesForTable(rules.body);

					this.dataSource.data = data;
					this.dataSource.paginator = this.paginator;
				},
				err => {
					console.log("Error fetching rules");
					console.log(err);
					this.error = true;
				},
			);
	}

	getRulesForTable(rules: MirRuleHttp[]): MirRule[] {
		if (!rules || !Array.isArray(rules)) {
			return [];
		}

		return rules.length
			? rules.map(r => {
					const formattedRule = this.rulesService.formatRuleForDisplay(r);
					return {
						...formattedRule,
						checkboxDisabled: formattedRule.assignedAssets > 0,
					};
			  })
			: [];
	}

	getDataSource() {
		return this.dataSource;
	}

	onMatSortChange(event: Sort) {
		this.selectionModel?.clear();

		this.currentRuleApiParams = {
			...this.currentRuleApiParams,
			sort: event.active,
			sortDir: event.direction,
		};
		this._ruleApiParams$.next(this.currentRuleApiParams);
	}

	receiveSortObj(sort: MatSort) {
		this.sort = sort;
		this.sort.disableClear = true;
		this.getDataSource().sort = sort;
	}

	ngOnDestroy() {
		this.onDestroy$.next();
		this.onDestroy$.complete();
	}

	pageQueryChange(e) {
		const page = e.get("page");
		const perPage = e.get("perPage");
		this.currentRuleApiParams = {
			...this.currentRuleApiParams,
			page: page.toString(),
			perPage: perPage.toString(),
		};
		this.selectedRows = [];

		this._ruleApiParams$.next(this.currentRuleApiParams);
	}

	inputChanged(search: string) {
		this.currentRuleApiParams = {
			...this.currentRuleApiParams,
			name: search,
			userName: search,
		};
		this._ruleApiParams$.next(this.currentRuleApiParams);
	}

	get ruleIds() {
		return this.selectedRows.map(r => r.id);
	}

	deleteButtonClick(ruleIds: string[]) {
		// open confirmation dialog
		const dialogRef = this.dialog.open(ConfirmDeleteRuleDialogComponent, {
			autoFocus: true,
			restoreFocus: true,
		});

		dialogRef.afterClosed().subscribe(() => {
			setTimeout(() => {
				const triggerEl = document.getElementById("delete-rules-btn");
				if (triggerEl) {
					// handle potential Shadow DOM from app-action-button
					let innerBtn = triggerEl.querySelector<HTMLButtonElement>("button");
					if (!innerBtn && triggerEl.shadowRoot) {
						innerBtn = triggerEl.shadowRoot.querySelector<HTMLButtonElement>("button");
					}
					if (innerBtn && !innerBtn.disabled) {
						innerBtn.focus();
					} else {
						// fallback to focusing the component instance if it's focusable natively
						const triggerAsBtn = triggerEl as any;
						if (typeof triggerAsBtn.focus === "function" && !triggerAsBtn.disabled) {
							triggerAsBtn.focus();
						} else {
							// If button is disabled (because rules were deleted and selection cleared),
							// move focus back to the top of the table area like the search input
							const searchInput = document.querySelector<HTMLElement>(".search-container input");
							if (searchInput) {
								searchInput.focus();
							}
						}
					}
				}
			});
		});
		this.rulesService.confirmDeleteRules$
			.pipe(
				switchMap(_ => {
					return this.rulesService.deleteRules({ companyId: this.companyId, ruleIds });
				}),
				takeUntil(this.onDestroy$),
			)
			.subscribe(
				_ => {
					this._ruleApiParams$.next({
						...this.currentRuleApiParams,
					});
					this.table.selectionModel.clear();
					this.selectedRows = [];
					this.rowsDeleted.emit();
				},
				err => {
					console.log("Error occurred deleting rules:");
					console.log(err);
					this.table.selectionModel.clear();
					this.selectedRows = [];
					this.error = true;
				},
			);
	}

	disableDataCheckbox(event: { data: MirRule; checkbox: MatCheckbox | { disabled: boolean } }) {
		const { data, checkbox } = event;
		if (data.assignedAssets > 0) {
			checkbox.disabled = true;
			// Trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
			this.cdr.detectChanges();
		}
	}

	enableToolTip(event: { data: MirRule; toolTip: MatTooltip | { message: string } }) {
		const { data, toolTip } = event;
		if (data.assignedAssets > 0) {
			toolTip.message = "Cannot check this rule because it has assigned assets.";
		}
	}
}
